import { itemsApi } from "#/api/itemsApi";
import { ItemType, type Item } from "#/types/items";
import { productApi } from "#/api/productApi";
import { sitesApi } from "#/api/sitesApi";
import ActionButton from "#/components/buttons/ActionButton";
import LastMovedText from "#/components/ui/LastMovedText";
import Pagination from "#/components/buttons/Pagination";
import Popover from "#/components/overlays/Popover";
import type { TableSortProps } from "#/components/tables/BaseTable";
import SelectableTable from "#/components/tables/SelectableTable";
import { getRandomColor, getItemStatusColor } from "#/utils/ColorUtils";
import { sanitize, sanitizeItemStatusLabel } from "#/utils/StringUtils";
import { getDwellStatus } from "#/utils/dwell";
import { exportToCSV } from "#/utils/CsvUtils";
import { formatHawbLabel, HAWB_EXITED_STATUS } from "#/utils/manifestUtils";
import { logger } from "#/utils/logger";
import { Badge, Button, Select, Table, Text, TextInput, Title, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconDownload,
  IconExternalLink,
  IconFilter,
  IconLink,
  IconEye,
  IconPlus,
  IconRefresh,
  IconSearch,
  IconTrash,
  IconUpload,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useClient } from "#/hook/useClient";
import { formatDate, formatDateTimeForExport, formatLastMovedForExport, formatTime } from "#/utils/dateTimeUtils";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDebouncedValue } from "@mantine/hooks";
import ExportConfirmationModal from "#/components/overlays/ExportConfirmationModal";
import CreateTicketDrawer from "./components/CreateTicketDrawer";
import ImportCSVModal from "./components/ImportCSVModal";
import NotifyPopover from "#/components/overlays/NotifyPopover";
import SelectTemplateModal from "./components/SelectTemplateModal";
import { usePermission } from "#/hooks/usePermission";
import { useShipmentManifestsEnabled } from "#/hooks/useShipmentManifestsEnabled";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { useAllowedStatuses } from "#/hooks/useAllowedStatuses";
import { useTableColumns, type ColumnRenderer } from "#/hooks/useTableColumns";
import type { ColumnKey } from "#/config/tableColumnsConfig";
import BulkDeleteModal from "./components/BulkDeleteModal";
import BulkUpdateStatusModal from "./components/BulkUpdateStatusModal";
import AssignAwbModal from "./components/AssignAwbModal";

export interface Props {
  withoutActions?: boolean;
}


/** Stable `exclude` values for useTableColumns (a new array each render would re-memo). */
const NO_EXCLUDED_COLUMNS: readonly ColumnKey<"items">[] = [];
const MANIFEST_ONLY_COLUMNS: readonly ColumnKey<"items">[] = ["hawb"];

const ItemsPage = ({ withoutActions = false }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { client } = useClient();
  const { canWrite, canDelete } = usePermission();
  const allowedStatuses = useAllowedStatuses();
  const [searchParams, setSearchParams] = useSearchParams();

  const [status, setStatus] = useState<string | undefined>(searchParams.get('status') || undefined);
  const [categoryId, setCategoryId] = useState<string | undefined>(searchParams.get('categoryId') || undefined);
  const [siteId, setSiteId] = useState<string | undefined>(searchParams.get('siteId') || undefined);
  const [itemType, setItemType] = useState<string | undefined>(searchParams.get('itemType') || undefined);
  // "Immobilized" is a virtual backend filter (stuck past dwell time); only `true` means on.
  const [immobilized, setImmobilized] = useState<boolean>(searchParams.get('immobilized') === 'true');
  const [search, setSearch] = useState<string>(searchParams.get('search') || "");
  const [debouncedSearch] = useDebouncedValue(search, 500);

  // The active filters as the backend `filterBy` string, built once and shared
  // by the list query and Export All so the two can never disagree.
  const activeFilterBy = useMemo(() => {
    const filters: string[] = [];
    if (status) filters.push(`status=${status}`);
    if (categoryId) filters.push(`product_id=${categoryId}`);
    if (siteId) filters.push(`site_id=${siteId}`);
    if (itemType) filters.push(`type=${itemType}`);
    if (immobilized) filters.push("immobilized=true");
    return filters.join(",");
  }, [status, categoryId, siteId, itemType, immobilized]);

  // Update URL params when filters change
  useEffect(() => {
    const params: Record<string, string> = {};
    if (status) params.status = status;
    if (categoryId) params.categoryId = categoryId;
    if (siteId) params.siteId = siteId;
    if (itemType) params.itemType = itemType;
    if (immobilized) params.immobilized = 'true';
    if (search) params.search = search;
    // Replace, not push: typing in the search box must not fill the browser history.
    setSearchParams(params, { replace: true });
  }, [status, categoryId, siteId, itemType, immobilized, search, setSearchParams]);

  const [
    createMenuPopoverOpened,
    {
      open: openCreateMenuPopover,
      close: closeCreateMenuPopover,
      toggle: toggleCreateMenuPopover,
    },
  ] = useDisclosure(false);

  const [
    createTicketDrawerOpened,
    { open: openCreateTicketDrawer, close: closeCreateTicketDrawer },
  ] = useDisclosure(false);

  const [
    importCsvModalOpened,
    {
      open: openImportCsvModal,
      close: closeImportCsvModal,
      toggle: toggleImportCsvModal,
    },
  ] = useDisclosure(false);

  const [
    filtersOpened,
    { open: openFilters, close: closeFilters, toggle: toggleFilters },
  ] = useDisclosure(false);

  const [
    templateModalOpened,
    { open: openTemplateModal, close: closeTemplateModal },
  ] = useDisclosure(false);

  const [
    exportModalOpened,
    { open: openExportModal, close: closeExportModal },
  ] = useDisclosure(false);

  const [
    bulkUpdateStatusModalOpened,
    { open: openBulkUpdateStatusModal, close: closeBulkUpdateStatusModal },
  ] = useDisclosure(false);

  const [
    bulkDeleteModalOpened,
    { open: openBulkDeleteModal, close: closeBulkDeleteModal },
  ] = useDisclosure(false);

  const [
    assignAwbModalOpened,
    { open: openAssignAwbModal, close: closeAssignAwbModal },
  ] = useDisclosure(false);

  const [selectedItemsIds, setSelectedItemsIds] = useState<number[]>([]);
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(25);
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");
  // One definition per column: header label and visibility come from
  // tableColumnsConfig.items, the cell and the backend sort key live here.
  // `sortKey` values are the exact `sortBy` strings the backend accepted before.
  const itemColumns: Record<ColumnKey<"items">, ColumnRenderer<Item>> = {
    createdAt: {
      sortKey: "createdAt",
      cellClassName: "min-w-[140px]",
      cell: (item) =>
        item.createdAt ? (
          <span>
            {formatDate(item.createdAt, client)}
            <br />
            {formatTime(item.createdAt, client)}
          </span>
        ) : (
          "--"
        ),
    },
    external_id: {
      sortKey: "external_id",
      cellClassName: "min-w-[140px]",
      cell: (item) => item.external_id || "--",
    },
    last_scan: {
      sortKey: "last_scan",
      cellClassName: "min-w-[140px]",
      cell: (item) =>
        item.last_scan_data && item.last_scan_data.time ? (
          <div className="w-full flex flex-col">
            <span>
              {formatDate(item.last_scan_data.time, client)}
              <br />
              {formatTime(item.last_scan_data.time, client)}
            </span>
            <div className="w-full -mt-1  flex mt-2">
              <Badge
                color={item.last_scan_data.type ? "lime" : "grape"}
                variant="outline"
                size="sm"
                className="mt-2"
              >
                {item.last_scan_data.type}
              </Badge>
            </div>
          </div>
        ) : (
          "--"
        ),
    },
    last_moved: {
      sortKey: "last_moved",
      cellClassName: "min-w-[140px]",
      cell: (item) => {
        const dwell = getDwellStatus(item, client);
        if (dwell.isStuck) {
          return (
            <Tooltip
              multiline
              w={240}
              withArrow
              label={t("items.dwell.stuckTooltip", {
                days: dwell.idleDays,
                dwell: dwell.dwellDays,
              })}
            >
              <span className="text-red-600 font-semibold cursor-help">
                <LastMovedText date={item.last_moved} />
              </span>
            </Tooltip>
          );
        }
        return <LastMovedText date={item.last_moved} />;
      },
    },
    // RFID tags first, with the item type on the line below.
    rfid_tags: {
      cellClassName: "min-w-[180px]",
      cell: (item) => (
        <div className="flex flex-col items-start gap-2">
          {item.rfid_tags && item.rfid_tags.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              <Badge
                color={getRandomColor(item.rfid_tags[0].rfid)}
                variant="light"
                size="sm"
              >
                {item.rfid_tags[0].rfid}
              </Badge>
              {item.rfid_tags.length > 1 && (
                <Popover
                  withArrow
                  withOverlay={true}
                  anchor={
                    <Badge
                      color="gray"
                      variant="white"
                      size="sm"
                      className="cursor-pointer"
                    >
                      {t("items.tags.more", { count: item.rfid_tags.length - 1 })}
                    </Badge>
                  }
                >
                  <div className="flex flex-col gap-2 ">
                    <span className="text-xs font-semibold text-slate-600">
                      {t("items.tags.all", { count: item.rfid_tags.length })}
                    </span>
                    <div className="flex flex-col gap-1 max-w-sm">
                      {item.rfid_tags.map((tag) => (
                        <Badge
                          key={tag.id}
                          color={getRandomColor(tag.rfid)}
                          variant="dot"
                          size="md"
                          className="font-mono"
                        >
                          {tag.rfid}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </Popover>
              )}
            </div>
          ) : (
            <span className="text-gray-400">--</span>
          )}

          <div className="flex items-center gap-2">
            <Badge
              color={
                item.type === ItemType.CAGE
                  ? "violet"
                  : item.type === ItemType.CAGE_ITEM
                    ? "cyan"
                    : "gray"
              }
              variant="light"
              size="sm"
            >
              {item.type ? sanitize(item.type) : sanitize("item")}
            </Badge>
            {item.type === ItemType.CAGE_ITEM && item.cage && (
              <Button
                variant="transparent"
                size="compact-xs"
                color="black"
                className="underline px-0"
                rightSection={<IconExternalLink size={12} />}
                onClick={() =>
                  window.open(`/inventory/items/${item?.cage?.id}`, "_blank")
                }
              >
                {t("items.viewCage")}
              </Button>
            )}
          </div>
        </div>
      ),
    },
    status: {
      sortKey: "status",
      cellClassName: "min-w-[150px]",
      cell: (item) =>
        item?.status ? (
          <Badge
            color={getItemStatusColor(item.status)}
            variant="light"
            size="md"
          >
            {sanitize(item.status)}
          </Badge>
        ) : (
          "--"
        ),
    },
    category: {
      cellClassName: "min-w-[150px]",
      cell: (item) =>
        item?.product?.name ? (
          <Badge
            color={getRandomColor(item.product.name)}
            variant="light"
            size="md"
            className="cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => window.open(`/inventory/categories/${item.product.id}`, '_blank')}
            rightSection={<IconExternalLink size={12} className="opacity-60" />}
          >
            {item.product.name}
          </Badge>
        ) : (
          "--"
        ),
    },
    location: {
      cellClassName: "min-w-[200px]",
      cell: (item) => item.location ?? "--",
    },
    related_count: {
      cellClassName: "min-w-[90px]",
      cell: (item) => (item.related_count ? item.related_count : "--"),
    },
    times_scanned: {
      sortKey: "times_scanned",
      cellClassName: "min-w-[110px]",
      // 0 is a real count (never scanned); only a missing value shows "--".
      cell: (item) => (typeof item.times_scanned === "number" ? item.times_scanned : "--"),
    },
    hawb: {
      sortKey: "hawb",
      cellClassName: "min-w-[180px]",
      cell: (item) =>
        item.hawb ? (
          <Badge
            color="indigo"
            variant="light"
            size="md"
            className="cursor-pointer hover:opacity-80 transition-opacity normal-case"
            onClick={() => window.open(`/fleet/shipment-manifests/${item.hawb?.id}`, "_blank")}
            rightSection={<IconExternalLink size={12} className="opacity-60" />}
          >
            {formatHawbLabel(item.hawb)}
          </Badge>
        ) : (
          "--"
        ),
    },
  };
  // The AWB / HAWB column only exists for clients with Shipment Manifests on.
  const manifestsEnabled = useShipmentManifestsEnabled();
  // "exited" is set only by a C72 Validate exit, so it is offered for
  // filtering here but never added to the client's allowed (pickable) statuses.
  const allowedStatusList: string[] = allowedStatuses;
  const statusFilterOptions: string[] =
    manifestsEnabled && !allowedStatusList.includes(HAWB_EXITED_STATUS)
      ? [...allowedStatusList, HAWB_EXITED_STATUS]
      : allowedStatusList;
  // Without actions there is no selection checkbox, so no leading column.
  const { visible, headers, sortableColumns, sortKeyAt, indexOf } = useTableColumns(
    "items",
    itemColumns,
    {
      leadingColumns: withoutActions ? 0 : 1,
      exclude: manifestsEnabled ? NO_EXCLUDED_COLUMNS : MANIFEST_ONLY_COLUMNS,
    },
  );
  const [sortBy, setSortBy] = useState<string>("last_scan");

  const prepareExportData = (itemsToExport: typeof items) => {
    return itemsToExport.map((item) => ({
      createdAt: formatDateTimeForExport(item.createdAt),
      external_id: item.external_id || "",
      last_scan: formatDateTimeForExport(item.last_scan_data?.time),
      last_scan_type: item.last_scan_data?.type || "",
      last_moved: formatLastMovedForExport(item.last_moved, t),
      item_type: item.type ? sanitize(item.type) : "Item",
      rfid_tags: item.rfid_tags?.map((tag) => tag.rfid).join("; ") || "",
      status: sanitize(item.status) || "",
      product: item.product?.name || "",
      location: item.location || "",
      stolen: item.stolen ? "Yes" : "No",
      related_count: String(item.related_count ?? 0),
      times_scanned:
        typeof item.times_scanned === "number" ? String(item.times_scanned) : "",
      hawb: item.hawb ? formatHawbLabel(item.hawb) : "",
    }));
  };

  const exportHeaders = [
    "createdAt",
    "external_id",
    "last_scan",
    "last_scan_type",
    "last_moved",
    "item_type",
    "rfid_tags",
    "status",
    "product",
    "location",
    "stolen",
    "related_count",
    "times_scanned",
    "hawb",
  ] as const;

  // Opt-in columns are exported only when the client sees them: AWB / HAWB
  // with Shipment Manifests, "Times scanned" when enabled in Column Visibility
  // (the backend only returns the count then).
  const timesScannedVisible = visible.some((column) => column.key === "times_scanned");
  const activeExportHeaders: string[] = exportHeaders.filter(
    (header) =>
      (header !== "hawb" || manifestsEnabled) &&
      (header !== "times_scanned" || timesScannedVisible),
  );

  const exportHeaderLabels: Record<string, string> = {
    createdAt: t("items.columns.dateOfCreation"),
    external_id: t("items.columns.externalId"),
    last_scan: t("items.columns.lastScan"),
    last_scan_type: t("items.columns.lastScanType"),
    last_moved: t("items.columns.lastMoved"),
    item_type: t("items.columns.type"),
    rfid_tags: t("items.columns.rfidTags"),
    status: t("items.columns.status"),
    product: t("items.columns.category"),
    location: t("items.columns.location"),
    stolen: t("items.columns.stolen"),
    related_count: t("items.columns.linkedItems"),
    times_scanned: t("items.columns.timesScanned"),
    hawb: t("items.columns.hawb"),
  };

  const handleExportCurrentPage = () => {
    if (!items || items.length === 0) return;

    const exportData = prepareExportData(items);
    const note = t("common.utcNote");
    const filename = `items-export-${moment().format("YYYY-MM-DD-HHmmss")}.csv`;
    exportToCSV(exportData, activeExportHeaders, filename, exportHeaderLabels, note);
  };

  const handleExportAll = async () => {
    try {
      // Same filters AND search as the list, so the file matches the screen.
      const response = await itemsApi.getAll({
        page: 1,
        limit: total,
        sortBy: sortBy,
        order: sortDirection,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(activeFilterBy ? { filterBy: activeFilterBy } : {}),
      });

      const allItems = response.data.data;
      const exportData = prepareExportData(allItems);

      const note = t("common.utcNote");
      const filename = `items-export-all-${moment().format("YYYY-MM-DD-HHmmss")}.csv`;
      exportToCSV(exportData, activeExportHeaders, filename, exportHeaderLabels, note);
    } catch (error) {
      logger.error("Failed to export all items:", error);
    }
  };

  const { data: { products: categories = [] } = {} } = useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      const response = await productApi.getAll({
        page: 1,
        limit: 1000,
      });
      if (!response.data) {
        throw new Error("Failed to fetch categories");
      }
      return {
        products: response.data.data,
      };
    },
    enabled: true,
    staleTime: 1000 * 60 * 5,
  });

  const { data: { sites = [] } = {} } = useQuery({
    queryKey: ["sites"],
    queryFn: async () => {
      const response = await sitesApi.getAll({
        page: 1,
        limit: 1000,
      });
      if (!response.data) {
        throw new Error("Failed to fetch sites");
      }
      return {
        sites: response.data.data,
      };
    },
    enabled: true,
    staleTime: 1000 * 60 * 5,
  });

  const {
    data: { items = [], total = 0, per_page = 0 } = {},
    isFetching,
    refetch: getItems,
  } = useQuery({
    queryKey: ["items", status, categoryId, siteId, itemType, immobilized, sortDirection, sortBy, page, limit, debouncedSearch],
    queryFn: async () => {
      const response = await itemsApi.getAll({
        page: page,
        limit: limit,
        sortBy: sortBy,
        order: sortDirection,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(activeFilterBy ? { filterBy: activeFilterBy } : {}),
      });
      if (!response.data) {
        throw new Error("Failed to fetch categories");
      }
      return {
        items: response.data.data,
        total: response.data.total,
        per_page: response.data.limit,
      };
    },
    enabled: true,
    staleTime: 1000 * 60,
  });

  return (
    <div className="w-full h-full flex flex-col overflow-hidden gap-2 relative">
        <div className="w-full flex flex-col gap-3">
          <div className="w-full flex  items-center justify-between">
            <Title order={4}>{t("items.title")}</Title>
            <div className="gap-2 flex items-center ml-auto">
              <Popover
                classNames={{
                  overlay: "backdrop-blur-none",
                }}
                opened={createMenuPopoverOpened}
                onChange={toggleCreateMenuPopover}
                anchor={
                  <PermissionTooltip disabled={!canWrite()}>
                    <Button
                      leftSection={<IconPlus size={18} />}
                      color="black"
                      onClick={() => openCreateMenuPopover()}
                      disabled={!canWrite()}
                    >
                      {t("common.create")}
                    </Button>
                  </PermissionTooltip>
                }
              >
                <div className="flex flex-col gap-2">
                  <Tooltip
                    label={!canWrite() ? t("common.youDontHavePermission") : t("items.selectAtLeastOne")}
                    withArrow
                    disabled={selectedItemsIds.length > 0 && canWrite()}
                  >
                    <Button
                      leftSection={<IconPlus size={18} />}
                      variant="outline"
                      onClick={() => {
                        closeCreateMenuPopover();
                        openCreateTicketDrawer();
                      }}
                      disabled={!selectedItemsIds || selectedItemsIds.length <= 0 || !canWrite()}
                      color="black"
                    >
                      {t("items.createTicket")}
                    </Button>
                  </Tooltip>

                  <PermissionTooltip disabled={!canWrite()}>
                    <Button
                      color="black"
                      leftSection={<IconPlus size={18} />}
                      onClick={() => {
                        void navigate(`/inventory/items/new`);
                      }}
                      disabled={!canWrite()}
                    >
                      {t("items.createAnItem")}
                    </Button>
                  </PermissionTooltip>

                  <PermissionTooltip disabled={!canWrite()}>
                    <Button
                      color="black"
                      variant="outline"
                      leftSection={<IconPlus size={18} />}
                      onClick={() => {
                        closeCreateMenuPopover();
                        openTemplateModal();
                      }}
                      disabled={!canWrite()}
                    >
                      {t("items.createFromTemplate")}
                    </Button>
                  </PermissionTooltip>
                </div>
              </Popover>
              <PermissionTooltip disabled={!canWrite()}>
                <Button
                  leftSection={<IconUpload size={18} />}
                  color="green.5"
                  onClick={() => openImportCsvModal()}
                  disabled={!canWrite()}
                >
                  {t("common.importCsv")}
                </Button>
              </PermissionTooltip>
              <Button
                leftSection={<IconDownload size={18} />}
                variant="outline"
                onClick={openExportModal}
                disabled={!items || items.length === 0}
              >
                {t("common.exportCsv")}
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-2 justify-between">
            <TextInput
              className="flex-1 max-w-96"
              placeholder={t("items.searchPlaceholder")}
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={(e) => {
                setSearch(e.currentTarget.value);
                setPage(1);
              }}
            />
            <div className="flex items-center gap-2 ml-auto">
              <Popover
              opened={filtersOpened}
              onChange={toggleFilters}
              anchor={
                <Button
                  leftSection={<IconFilter size={18} />}
                  variant={status || categoryId || siteId || itemType || immobilized ? "filled" : "outline"}
                  color="black"
                  onClick={openFilters}
                >
                  {t("common.filters")}
                </Button>
              }
            >
              <div className="flex flex-col gap-4 min-w-64">
                <Select
                  label={t("items.filters.status")}

                  placeholder={t("common.selectStatus")}
                  value={status}
                  onChange={(value) => {
                    setPage(1);
                    setStatus(value ?? undefined);
                  }}
                  data={statusFilterOptions.map((item) => ({
                    value: item,
                    label: sanitizeItemStatusLabel(item),
                  }))}
                  classNames={{
                    label: "text-xs font-bold text-slate-600",
                  }}
                  clearable
                  searchable
                />

                <Select
                  label={t("items.filters.category")}

                  placeholder={t("items.form.categoryPlaceholder")}
                  value={categoryId}
                  onChange={(value) => {
                    setPage(1);
                    setCategoryId(value ?? undefined);
                  }}
                  data={categories.map((category) => ({
                    value: category.id.toString(),
                    label: category.name,
                  }))}
                  classNames={{
                    label: "text-xs font-bold text-slate-600",
                  }}
                  clearable
                  searchable
                />

                <Select
                  label={t("items.filters.site")}

                  placeholder={t("common.selectSite")}
                  value={siteId}
                  onChange={(value) => {
                    setPage(1);
                    setSiteId(value ?? undefined);
                  }}
                  data={sites.map((site) => ({
                    value: site.id.toString(),
                    label: site.name,
                  }))}
                  classNames={{
                    label: "text-xs font-bold text-slate-600",
                  }}
                  clearable
                  searchable
                />

                <Select
                  label={t("items.filters.type")}

                  placeholder={t("items.filters.type")}
                  value={itemType}
                  onChange={(value) => {
                    setPage(1);
                    setItemType(value ?? undefined);
                  }}
                  data={[
                    { value: "item", label: sanitize("item") },
                    { value: "cage", label: sanitize("cage") },
                    { value: "cage_item", label: sanitize("cage_item") },
                  ]}
                  classNames={{
                    label: "text-xs font-bold text-slate-600",
                  }}
                  clearable
                  searchable
                />

                <Select
                  label={t("items.filters.immobilized")}
                  description={t("items.filters.immobilizedDescription")}
                  placeholder={t("items.filters.immobilizedPlaceholder")}
                  value={immobilized ? "true" : null}
                  onChange={(value) => {
                    setPage(1);
                    setImmobilized(value === "true");
                  }}
                  data={[{ value: "true", label: t("items.filters.immobilizedYes") }]}
                  classNames={{
                    label: "text-xs font-bold text-slate-600",
                  }}
                  clearable
                />

                <Button
                  fullWidth
                  color="black"
                  variant="outline"
                  disabled={!status && !categoryId && !siteId && !itemType && !immobilized}
                  onClick={() => {
                    setPage(1);
                    setStatus(undefined);
                    setCategoryId(undefined);
                    setSiteId(undefined);
                    setItemType(undefined);
                    setImmobilized(false);
                    closeFilters();
                  }}
                >
                  {t("items.filters.clearAll")}
                </Button>
              </div>
            </Popover>
            <Pagination
              page={page}
              total={total}
              limit={per_page}
              onChange={setPage}
              onLimitChange={(newLimit) => {
                setLimit(newLimit);
                setPage(1);
              }}
            />
            </div>
          </div>
        </div>

      <div className="flex-1 overflow-auto">
        <SelectableTable
          headers={[...headers, t("common.actions")]}
          loading={isFetching}
          selectedIndices={selectedIndices}
          onSelectionChange={(newIndices) => {
            const selectedItems = newIndices.map((index) => items[index]);
            const ids = selectedItems.map((item) => item.id);
            setSelectedItemsIds(ids);
            setSelectedIndices(newIndices);
          }}
          allowSorting
          sortableColumns={sortableColumns}
          onSortChange={(e: TableSortProps) => {
            const key = sortKeyAt(e.index);
            if (!key) return;
            setPage(1);
            setSortDirection(e.direction);
            setSortBy(key);
          }}
          defaultSort={
            indexOf("last_scan") !== undefined
              ? { direction: "DESC", index: indexOf("last_scan") as number }
              : undefined
          }
          disabledSelection={withoutActions}
          rows={items.map((item) => {
            return (
              <Table.Tr key={item.id}>
                {visible.map((column) => (
                  <Table.Td key={column.key} className={column.cellClassName}>
                    {column.cell(item)}
                  </Table.Td>
                ))}
                <Table.Td className="min-w-[120px]">
                  <div className="grid grid-cols-2 gap-4">
                    <NotifyPopover item={item} />
                    <ActionButton
                      icon={IconEye}
                      variant="outline"
                      onClick={() => {
                        void navigate(`/inventory/items/${item.id}`);
                      }}
                    />
                  </div>
                </Table.Td>
              </Table.Tr>
            );
          })}
        />
      </div>

      <CreateTicketDrawer
        itemIds={selectedItemsIds}
        opened={createTicketDrawerOpened}
        onClose={() => closeCreateTicketDrawer()}
        onSuccess={() => {
          closeCreateTicketDrawer();
          setSelectedItemsIds([]);
          setSelectedIndices([]);
        }}
      />

      <ImportCSVModal
        opened={importCsvModalOpened}
        onClose={() => closeImportCsvModal()}
        onChange={toggleImportCsvModal}
        onSuccess={() => {
          closeImportCsvModal();
          void getItems();
        }}
      />

      <SelectTemplateModal
        opened={templateModalOpened}
        onClose={closeTemplateModal}
      />

      <ExportConfirmationModal
        opened={exportModalOpened}
        onClose={closeExportModal}
        onExportCurrentPage={() => Promise.resolve(handleExportCurrentPage())}
        onExportAll={handleExportAll}
        currentPageCount={items.length}
        totalCount={total}
      />

      <BulkUpdateStatusModal
        opened={bulkUpdateStatusModalOpened}
        onClose={closeBulkUpdateStatusModal}
        selectedItems={items.filter((item) => selectedItemsIds.includes(item.id))}
        onSuccess={() => {
          setSelectedItemsIds([]);
          setSelectedIndices([]);
          void getItems();
        }}
      />

      {manifestsEnabled && (
        <AssignAwbModal
          opened={assignAwbModalOpened}
          onClose={closeAssignAwbModal}
          itemIds={selectedItemsIds}
          onSuccess={() => {
            setSelectedItemsIds([]);
            setSelectedIndices([]);
            void getItems();
          }}
        />
      )}

      <BulkDeleteModal
        opened={bulkDeleteModalOpened}
        onClose={closeBulkDeleteModal}
        selectedItemIds={selectedItemsIds}
        onSuccess={() => {
          setSelectedItemsIds([]);
          setSelectedIndices([]);
          void getItems();
        }}
      />

      {selectedItemsIds.length > 0 && (
        <div className="absolute bottom-0 left-0 inset-x-0 z-50 flex items-center justify-between w-full bg-black/10 border-t border-black/50 backdrop-blur-sm p-4">
          <div className="flex items-center gap-2">
            <Text size="md" fw={600} c="dark">
              {selectedItemsIds.length} {t("common.selected")}
            </Text>
          </div>
          <div className="flex items-center gap-3">
            <PermissionTooltip disabled={!canWrite()}>
              <Button
                leftSection={<IconRefresh size={16} />}
                color="black"
                variant="filled"
                size="md"
                onClick={openBulkUpdateStatusModal}
                disabled={!canWrite()}
              >
                {t("items.updateStatus")}
              </Button>
            </PermissionTooltip>
            {manifestsEnabled && (
              <PermissionTooltip disabled={!canWrite()}>
                <Button
                  leftSection={<IconLink size={16} />}
                  color="black"
                  variant="filled"
                  size="md"
                  onClick={openAssignAwbModal}
                  disabled={!canWrite()}
                >
                  {t("items.assignAwb.button")}
                </Button>
              </PermissionTooltip>
            )}
            <PermissionTooltip disabled={!canDelete()}>
              <Button
                leftSection={<IconTrash size={16} />}
                color="red"
                variant="filled"
                size="md"
                onClick={openBulkDeleteModal}
                disabled={!canDelete()}
              >
                {t("common.delete")}
              </Button>
            </PermissionTooltip>
          </div>
        </div>
      )}
    </div>
  );
};

export default ItemsPage;
